import { memo, useMemo } from 'react'
import { alpha, TableRow } from '@mui/material'
import { useSelector } from 'react-redux'
import { OptionType } from '../../../../proto/generated/security'
import { isOptionChainLiveView, OptionChainViewKey } from '../../../../assets/constants'
import { RootState } from '../../../../store'
import { selectOptionChainRowOrderClassSuffix } from '../../../../selectors/strategyBuilder'
import { optionTradeDataType } from '../../../typings/options.typings'
import { OptionChainExpandedTableProps } from './optionChainExpandedTable'
import { useOptionChainHistoricalOrderMap } from './OptionChainOrderHighlightContext'
import { TableRowComponentProps } from '../../../../components/customTable/CustomTable'

type Props = TableRowComponentProps<optionTradeDataType> & {
    tableColumnType: OptionChainExpandedTableProps['type']
    optionChainViewKey: OptionChainViewKey
}

const OptionChainTableRow = ({
    row,
    index,
    className: staticClassName,
    onClick,
    children,
    rowRef,
    tableColumnType,
    optionChainViewKey,
}: Props) => {
    const historicalOrderMap = useOptionChainHistoricalOrderMap()

    const highlightSide = useMemo((): 'call' | 'put' | null => {
        if (tableColumnType === 'callOption') return 'call'
        if (tableColumnType === 'putOption') return 'put'
        if (tableColumnType === 'itmOption' || tableColumnType === 'otmOption') {
            const opt = tableColumnType === 'itmOption' ? row.itmOption : row.otmOption
            return opt?.security?.optionType === OptionType.CE ? 'call' : 'put'
        }
        return null
    }, [row.itmOption, row.otmOption, tableColumnType])

    const liveOrderSuffix = useSelector((state: RootState) =>
        isOptionChainLiveView(optionChainViewKey)
            ? selectOptionChainRowOrderClassSuffix(state, row.key, highlightSide)
            : ''
    )

    const historicalOrderSuffix = useMemo(() => {
        if (optionChainViewKey !== OptionChainViewKey.HISTORICAL || !historicalOrderMap || !highlightSide) return ''
        const data = historicalOrderMap[row.key]
        const orderSide = data?.[highlightSide]
        if (!orderSide) return ''
        return highlightSide === 'call'
            ? ` call-${orderSide}-order-row `
            : ` put-${orderSide}-order-row `
    }, [historicalOrderMap, highlightSide, optionChainViewKey, row.key])

    const className = staticClassName + (isOptionChainLiveView(optionChainViewKey) ? liveOrderSuffix : historicalOrderSuffix)

    return (
        <TableRow
            ref={rowRef}
            sx={{
                position: 'relative',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                '&:hover .MuiTableCell-root': {
                    backgroundColor: (theme) =>
                        theme.palette.mode === 'dark'
                            ? alpha(theme.palette.common.white, 0.03)
                            : alpha(theme.palette.common.black, 0.02),
                },
                '&.selected-row .MuiTableCell-root': {
                    backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#181818' : '#f4f4f4'),
                },
            }}
            className={className}
            onClick={onClick}
        >
            {children}
        </TableRow>
    )
}

export default memo(OptionChainTableRow)
